import '../search-info/search-info.scss';
import React, {useCallback} from 'react';
import {
    useCurrentSortType,
    useSearchPageType,
    useStateChat,
    useStateDocs,
    useStateQuery,
    useStateQueryPending,
} from '../../search-hooks';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {sortResults, startSearchChatAction} from '../../duck/search-actions';
import {useTranslation} from 'react-i18next';
import {ActiveDrawerContent, SearchSortType, SortOption, SortType} from '../../search-types';
import {SortDropdown} from '../../../common/sort-dropdown/sortDropdown';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {Icons} from '../../../icons/icons';
import {HibuButton} from '../../../common/hibuButton/hibuButton';

export const SearchResultOverview = React.memo(() => {
    const searchPageType = useSearchPageType();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const navigate: NavigateFunction = useNavigate();
    const isAiChatEnabled = useSelector(
        (state: DefaultRootState) => state.config.uiConfig.aiAssistant.searchAssistant.enabled,
    );

    const docs = useStateDocs();
    const query = useStateQuery();
    const queryPending = useStateQueryPending();
    const currentSortType = useCurrentSortType();
    const sortTypesFromConfiguration: SortType[] | undefined = useSelector(
        (state: DefaultRootState) => state.search[searchPageType].sortTypes,
    );
    const activeDrawerContent: ActiveDrawerContent = useStateChat();

    const onSortChange = useCallback(
        (selectedOption: SortOption) => {
            const currentSortType = {field: selectedOption.value, order: selectedOption.order};
            dispatch(sortResults(searchPageType, currentSortType, navigate));
        },
        [searchPageType, dispatch, navigate],
    );

    function onChatClick() {
        switch (activeDrawerContent) {
            case ActiveDrawerContent.PLACEHOLDER:
                dispatch(startSearchChatAction(searchPageType, true, ActiveDrawerContent.PLACEHOLDER));
                break;
            case ActiveDrawerContent.ASSISTANT:
                dispatch(startSearchChatAction(searchPageType, false, ActiveDrawerContent.ASSISTANT));
                break;
            case ActiveDrawerContent.PREVIEW:
                dispatch(startSearchChatAction(searchPageType, true, ActiveDrawerContent.PREVIEW));
                break;
            default:
                dispatch(startSearchChatAction(searchPageType, false, ActiveDrawerContent.PLACEHOLDER));
        }
    }

    const renderNoDocumentsFound = () => {
        return (
            <>
                {t('search.label.info.result_none', {searchPageType: searchPageType}) + ' '}
                <span className="highlight-query">{query}</span>
                <div className="search-hints">
                    <div className="search-hints-title">{t('search.label.info.search_hints_title')}</div>
                    <div className="search-hints-text">{t('search.label.info.search_hints_text1')}</div>
                    <div className="search-hints-text">{t('search.label.info.search_hints_text2')}</div>
                    <div className="search-hints-text">{t('search.label.info.search_hints_text3')}</div>
                </div>
            </>
        );
    };

    const options = () => {
        const options: SortOption[] = [];
        const sortTypesArray = sortTypesFromConfiguration ? sortTypesFromConfiguration : SearchSortType.getAll();
        sortTypesArray.forEach((type) => {
            options.push({
                value: type.field,
                order: type.order,
                label: t('search.label.sortBy.' + type.field),
            });
        });
        return options;
    };

    if (!docs.allKeys.length) {
        return <div id="no-results-overview-block">{!queryPending && renderNoDocumentsFound()}</div>;
    }

    return (
        <div id="search-results-overview-block">
            <HibuButton
                visible={isAiChatEnabled}
                onClick={onChatClick}
                icon={Icons.AI_ASSISTANT}
                tooltipKey={'assistant.search-chat-title'}
                label={t('assistant.chat')}
                className={'hibu-chat-button'}
                classNameIcon={'hibu-chat-button-icon'}
            />

            {!!docs.allKeys.length && (
                <SortDropdown
                    id="search-sort"
                    value={options().find((option) => option.value === currentSortType.field)}
                    onChange={onSortChange}
                    options={options()}
                />
            )}
        </div>
    );
});
